"use client"

import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { cn } from '../../lib/utils'
import { UIButton } from '../buttons/ui-button'
import { UIBadge } from '../display/ui-badge'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'
import { createMarkdownComponents } from '../../lib/markdown-components'
import {
  Copy,
  Check,
  RefreshCw
} from 'lucide-react'


// AI 응답 컴포넌트 Props - 단순화된 shadcn.io/ai Response 기반
export interface UIAIResponseProps {
  // 콘텐츠
  content: string
  isStreaming?: boolean
  isComplete?: boolean

  // 콘텐츠 타입
  contentType?: 'text' | 'markdown' | 'code'
  language?: string // 코드 하이라이팅용

  // 메타데이터
  confidence?: number

  // UI 옵션
  variant?: 'default' | 'compact' | 'detailed'
  showCopyUIButton?: boolean
  showRegenerateUIButton?: boolean
  showConfidence?: boolean

  // 마크다운 설정
  enableGfm?: boolean
  enableCodeHighlight?: boolean

  // 스타일링
  className?: string
  contentClassName?: string
  headerClassName?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'

  // 상호작용 콜백
  onCopy?: () => void
  onRegenerate?: () => void
}

/**
 * UIAIResponse - AI 응답을 마크다운으로 렌더링하는 컴포넌트
 *
 * shadcn.io/ai Response 컴포넌트 기반으로 구현된 고성능 AI 응답 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * <UIAIResponse
 *   content="# Hello World\nThis is a **markdown** response."
 *   variant="detailed"
 *   sources={sources}
 *   tools={tools}
 *   showCopyButton={true}
 * />
 * ```
 */
export const UIAIResponse = React.forwardRef<HTMLDivElement, UIAIResponseProps>(
  ({
    content,
    isStreaming = false,
    isComplete = true,
    contentType = 'markdown',
    language,
    confidence,
    variant = 'default',
    showCopyUIButton = true,
    showRegenerateUIButton = false,
    showConfidence = false,
    enableGfm = true,
    enableCodeHighlight = true,
    className,
    contentClassName,
    headerClassName,
    ariaLabel,
    locale = 'ko',
    onCopy,
    onRegenerate,
    ...props
  }, ref) => {
    const [copied, setCopied] = useState(false)

    // Prop validation
    if (!content && !isStreaming) {
      console.warn('UIAIResponse: content is required when not streaming')
    }

    if (confidence !== undefined && (confidence < 0 || confidence > 1)) {
      console.warn('UIAIResponse: confidence must be between 0 and 1')
    }

    // 복사 기능 with better error handling
    const handleCopy = React.useCallback(async () => {
      if (!content) {
        console.warn('UIAIResponse: No content to copy')
        return
      }

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
          onCopy?.()
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = content
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
          onCopy?.()
        }
      } catch (error) {
        console.error('Failed to copy content:', error)
      }
    }, [content, onCopy])

    // 마크다운 설정 - 공유 유틸리티 사용
    const markdownComponents = createMarkdownComponents(locale)

    // 신뢰도 색상
    const getConfidenceColor = (score?: number) => {
      if (!score) return 'text-muted-foreground'
      if (score >= 0.8) return 'text-green-800 dark:text-green-400'
      if (score >= 0.6) return 'text-yellow-600'
      return 'text-red-600'
    }


    return (
      <UIBox
        ref={ref}
        className={cn(
          "relative",
          // AI response container
          variant === 'compact' && "text-sm",
          variant === 'detailed' && "space-y-6 p-6 rounded-xl bg-card/30 dark:bg-card/20 border border-border/40 shadow-lg shadow-black/[0.02] dark:shadow-black/[0.08]",
          className
        )}
        aria-label={ariaLabel || (locale === 'ko' ? 'AI 응답' : 'AI Response')}
        {...props}
      >
        {/* 헤더 (상세 모드) */}
        {variant === 'detailed' && (
          <UIBox className={cn(
            "flex items-center justify-between pb-4 border-b border-border/30",
            headerClassName
          )}>
            <UIBox className="flex items-center gap-2">
              <UIBadge
                variant="outline"
                className="bg-background/50 dark:bg-background/30 border-border/60"
              >
                {locale === 'ko' ? 'AI 응답' : 'AI Response'}
              </UIBadge>

              {showConfidence && confidence !== undefined && (
                <UIBadge
                  variant="secondary"
                  className={cn(
                    getConfidenceColor(confidence),
                    "bg-background/50 dark:bg-background/30"
                  )}
                >
                  {locale === 'ko' ? '신뢰도' : 'Confidence'}: {Math.round(confidence * 100)}%
                </UIBadge>
              )}

              {isStreaming && (
                <UIBadge
                  variant="outline"
                  className="animate-pulse bg-background/50 dark:bg-background/30 border-border/60"
                >
                  {locale === 'ko' ? '응답 중...' : 'Responding...'}
                </UIBadge>
              )}
            </UIBox>

            <UIBox className="flex items-center gap-1">
              {showCopyUIButton && (
                <UIButton
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  disabled={!content}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  <UITypography as="span" variant="small" className="sr-only">
                    {locale === 'ko' ? '복사' : 'Copy'}
                  </UITypography>
                </UIButton>
              )}

              {showRegenerateUIButton && (
                <UIButton
                  variant="ghost"
                  size="sm"
                  onClick={onRegenerate}
                  disabled={isStreaming}
                >
                  <RefreshCw className="w-4 h-4" />
                  <UITypography as="span" variant="small" className="sr-only">
                    {locale === 'ko' ? '재생성' : 'Regenerate'}
                  </UITypography>
                </UIButton>
              )}
            </UIBox>
          </UIBox>
        )}


        {/* 메인 콘텐츠 */}
        <UIBox className={cn(
          "prose prose-gray max-w-none dark:prose-invert",
          "prose-headings:mt-8 prose-headings:mb-6 prose-p:my-6 prose-pre:my-8",
          "prose-ul:my-8 prose-ol:my-8 prose-blockquote:my-8",
          "prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg",
          variant === 'compact' && "prose-sm prose-headings:mt-6 prose-headings:mb-4 prose-p:my-4",
          variant === 'detailed' && "prose-lg prose-headings:mt-10 prose-headings:mb-8 prose-p:my-8",
          contentClassName
        )}>
          {contentType === 'markdown' ? (
            <ReactMarkdown
              remarkPlugins={enableGfm ? [remarkGfm] : []}
              rehypePlugins={enableCodeHighlight ? [rehypeHighlight] : []}
              components={markdownComponents}
            >
              {content}
            </ReactMarkdown>
          ) : contentType === 'code' ? (
            <UIBox className="relative my-4">
              <ReactMarkdown
                remarkPlugins={enableGfm ? [remarkGfm] : []}
                rehypePlugins={enableCodeHighlight ? [rehypeHighlight] : []}
                components={markdownComponents}
              >
                {`\`\`\`${language || 'jsx'}\n${content}\n\`\`\``}
              </ReactMarkdown>
            </UIBox>
          ) : (
            <UITypography
              as="div"
              className="whitespace-pre-wrap leading-relaxed"
            >
              {content}
            </UITypography>
          )}

          {/* 스트리밍 커서 */}
          {isStreaming && (
            <UIBox className="inline-block w-0.5 h-5 bg-primary animate-pulse ml-1 opacity-75" />
          )}
        </UIBox>


        {/* 컴팩트 모드 액션 버튼들 */}
        {variant !== 'detailed' && (showCopyUIButton || showRegenerateUIButton) && (
          <UIBox className="flex items-center justify-end gap-2 mt-4">
            {showCopyUIButton && (
              <UIButton
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                disabled={!content}
                aria-label={locale === 'ko' ? '복사' : 'Copy'}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                <UITypography as="span" variant="small" className="sr-only">
                  {locale === 'ko' ? '복사' : 'Copy'}
                </UITypography>
              </UIButton>
            )}

            {showRegenerateUIButton && (
              <UIButton
                variant="ghost"
                size="sm"
                onClick={onRegenerate}
                disabled={isStreaming}
                aria-label={locale === 'ko' ? '재생성' : 'Regenerate'}
              >
                <RefreshCw className="w-4 h-4" />
                <UITypography as="span" variant="small" className="sr-only">
                  {locale === 'ko' ? '재생성' : 'Regenerate'}
                </UITypography>
              </UIButton>
            )}
          </UIBox>
        )}
      </UIBox>
    )
  }
)

UIAIResponse.displayName = "UIAIResponse"